import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8 } from "styled-components";
import React from "react";
import { m } from "framer-motion";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { IconButton } from "~/features/ui/icon-button";
import CaretDown from "~/public/icons/caret-down.svg";
const activeStyles = {
  light: {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(227 227 240 / var(--tw-bg-opacity))"
  },
  dark: {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
  }
} as const;
const hoverStyles = {
  light: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(227 227 240 / var(--tw-bg-opacity))"
      }
    }
  },
  dark: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
      }
    }
  }
} as const;
export type FAQQuestionViewProps = {
  answer?: React.ReactNode;
  /** Anchor slug: the DOM id, the accordion value and the URL hash, all one string. */
  id?: string | null;
  isOpen: boolean;
  mode: Mode;
  question?: string | null;
  theme: Theme;
};
export function FAQQuestionView({
  answer,
  id,
  isOpen,
  mode,
  question,
  theme
}: FAQQuestionViewProps) {
  const themeStyle = getThemeStyle({
    mode,
    theme
  });
  const activeStyle = activeStyles[mode];
  const [answerHeight, setAnswerHeight] = React.useState(0);
  const answerRef = React.useRef<HTMLDivElement>(null);

  // The slide needs a pixel target, and Radix's own
  // `--radix-accordion-content-height` cannot supply it: Radix measures the
  // content element itself, and under `forceMount` our collapsed styles are
  // applied at measure time, so it reads the border height until the item has
  // been opened once. Measuring the inner wrapper instead is always correct —
  // it keeps its natural height regardless of what the clipped parent is doing —
  // and a ResizeObserver keeps it right through reflows and viewport changes.
  React.useEffect(() => {
    const node = answerRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      setAnswerHeight(entry?.contentRect.height ?? node.offsetHeight);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [answer]);
  return <_StyledRadixAccordionItem value={id || ""} id={id || undefined}
  // Clears the sticky header when the browser jumps to this anchor.
  >
      <_StyledRadixAccordionHeader $_css={[{
      "position": "relative",
      "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
      "transitionDuration": "250ms"
    }, themeStyle.background, hoverStyles[mode], isOpen && activeStyle]}>
        <_StyledMDiv animate={{
        opacity: isOpen ? 1 : 0
      }} />
        <_StyledRadixAccordionTrigger>
          {/* Radix renders `Header` as an h3, so the question text must not be a heading itself. */}
          {question && <_StyledSpan>{question}</_StyledSpan>}
          <IconButton as="div" variant="transparent" icon={{
          component: CaretDown,
          style: [{
            "--tw-text-opacity": "1",
            "color": "rgb(0 7 4 / var(--tw-text-opacity))",
            "transitionProperty": "transform",
            "transitionDuration": "250ms",
            "transitionTimingFunction": "ease-in-out"
          }, isOpen && {
            "--tw-rotate": "180deg",
            "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
          }]
        }} />
        </_StyledRadixAccordionTrigger>
      </_StyledRadixAccordionHeader>
      {/*
        `forceMount` keeps the answer in the server-rendered HTML whether or not
        it is expanded. AI crawlers (GPTBot, ClaudeBot, PerplexityBot) read raw
        HTML and do not run JS, so without this only the currently-open answer
        would ever be crawlable or citable.
         Radix computes `isOpen = context.open || isPresent`, and `forceMount`
        pins `isPresent` to true — so it applies neither `hidden` nor any height,
        and never unmounts. The shared `radix-accordion-expand/collapse`
        keyframes therefore cannot be used (they only ever ran on a freshly
        mounted node).
         Nothing may be animated on `Content` itself. Radix's measure step writes
        `style.transitionDuration = "0s"` and only restores it when its
        `isMountAnimationPrevented` flag is false — but that flag is initialised
        from `isOpen`, which `forceMount` makes true at first render, so the
        restore is skipped and the inline `0s` sticks. Every transition declared
        on that node is silently dead. The slide therefore lives on the wrapper
        below, which Radix never touches, against a height we measure ourselves.
         `visibility: hidden` (rather than `display: none`) keeps the answer in
        the served markup while taking its links out of the tab order and hiding
        them from screen readers.
       */}
      {/*
        The divider between question and answer is a semi-transparent border, so
        it composites against whatever sits behind it and needs the open
        background on this node as well as on the slider below — without it the
        line blends into the header above and reads as missing.
         It is also drawn only while open. This node used to be unmounted when
        collapsed, so a permanent border would add a hairline under every
        collapsed question and push each one down by a pixel.
       */}
      <_StyledRadixAccordionContent forceMount $_css2={[themeStyle.border, isOpen && [{
      "borderTopWidth": "1px"
    }, activeStyle]]}>
        {answer && <_StyledDiv style={{
        height: isOpen ? answerHeight : 0
      }} $_css3={[{
        "overflow": "hidden"
      }, {
        visibility: "hidden",
        // `visibility` interpolates discretely, but holds at whichever
        // endpoint is `visible` until the transition completes — so a
        // 250ms duration keeps the answer on screen for the whole
        // collapse and hides it exactly at the end. Do NOT express this
        // as a delayed zero-duration transition (`visibility 0s linear
        // 250ms`): Chrome leaves that transition permanently `running`,
        // the flip to hidden never lands, and every collapsed answer
        // stays in the tab order.
        transition: "height 250ms ease-in-out, background-color 250ms ease-in-out, visibility 250ms linear"
      }, isOpen && {
        visibility: "visible",
        // Opening must reveal the answer immediately, not 250ms in.
        transition: "height 250ms ease-in-out, background-color 250ms ease-in-out, visibility 0s linear"
      }, isOpen && activeStyle]}>
            <div ref={answerRef}>
              <_StyledDiv2 $_css4={[{
            "paddingLeft": "4rem",
            "paddingRight": "4rem",
            "paddingTop": "2.4rem",
            "paddingBottom": "2.4rem",
            "opacity": "0",
            "transitionProperty": "opacity",
            "transitionDelay": "200ms",
            "transitionDuration": "250ms",
            "transitionTimingFunction": "ease-in-out"
          }, isOpen && {
            "opacity": "1"
          }]}>
                {answer}
              </_StyledDiv2>
            </div>
          </_StyledDiv>}
      </_StyledRadixAccordionContent>
    </_StyledRadixAccordionItem>;
}
var _StyledRadixAccordionItem = _styled(RadixAccordion.Item).withConfig({
  componentId: "sc-sf-1vdt9eb-0"
})({
  "scrollMarginTop": "var(--header-height)"
});
var _StyledRadixAccordionHeader = _styled2(RadixAccordion.Header).withConfig({
  componentId: "sc-sf-1vdt9eb-1"
})`${p => p.$_css}`;
var _StyledMDiv = _styled3(m.div).withConfig({
  componentId: "sc-sf-1vdt9eb-2"
})({
  "position": "absolute",
  "left": "0",
  "right": "0",
  "top": "0",
  "height": "0.2rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
  "opacity": "0"
});
var _StyledRadixAccordionTrigger = _styled4(RadixAccordion.Trigger).withConfig({
  componentId: "sc-sf-1vdt9eb-3"
})({
  "display": "flex",
  "width": "100%",
  "alignItems": "center",
  "justifyContent": "space-between",
  "gap": "0.8rem",
  "padding": "1.6rem",
  "paddingLeft": "4rem",
  "textAlign": "left",
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem",
    "paddingTop": "2.4rem",
    "paddingBottom": "2.4rem"
  }
});
var _StyledSpan = _styled5("span").withConfig({
  componentId: "sc-sf-1vdt9eb-4"
})({
  "display": "block",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledRadixAccordionContent = _styled6(RadixAccordion.Content).withConfig({
  componentId: "sc-sf-1vdt9eb-5"
})`${p => p.$_css2}`;
var _StyledDiv = _styled7("div").withConfig({
  componentId: "sc-sf-1vdt9eb-6"
})`${p => p.$_css3}`;
var _StyledDiv2 = /*#__PURE__*/_styled8("div").withConfig({
  displayName: "faq-question___StyledDiv2",
  componentId: "sc-zc1iv2-0"
})(["", ""], p => p.$_css4);